/**
 * V5: 공휴일 관리 컴포넌트 공통 타입
 */

import {
  Holiday,
  HolidayType,
  HolidaySyncLog,
  CompanyCode,
  SyncStatus,
} from '../../../../shared/hooks/useHolidays';

// Re-export for convenience
export type { Holiday, HolidayType, HolidaySyncLog, CompanyCode, SyncStatus };

// ============================================================================
// 상수
// ============================================================================

export const HOLIDAY_TYPE_LABELS: Record<HolidayType, string> = {
  national: '법정공휴일',
  company: '회사행사',
  temporary: '임시휴일',
};

export const HOLIDAY_SOURCE_LABELS: Record<string, string> = {
  seed: '시드',
  manual: '수동',
  api: 'API',
};

export const SYNC_STATUS_LABELS: Record<SyncStatus, string> = {
  success: '성공',
  partial: '부분 성공',
  fail: '실패',
};

export const TRIGGERED_BY_LABELS: Record<string, string> = {
  scheduler: '자동',
  manual: '수동',
};

export const HOLIDAY_STATUS_LABELS: Record<string, string> = {
  active: '활성',
  archived: '삭제됨',
};

export const CURRENT_YEAR = new Date().getFullYear();
export const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR - 5 + i);

// 요일 라벨
export const DAY_OF_WEEK_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

// ============================================================================
// 뷰 모드 타입
// ============================================================================

export type ViewMode = 'list' | 'calendar';

// ============================================================================
// 필터 타입
// ============================================================================

export interface HolidayFilters {
  type: HolidayType | 'all';
  status: 'active' | 'all';
}

export const DEFAULT_FILTERS: HolidayFilters = {
  type: 'all',
  status: 'active',
};

// ============================================================================
// 정렬 타입
// ============================================================================

export type SortField = 'date' | 'name' | 'type';
export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export const DEFAULT_SORT: SortConfig = {
  field: 'date',
  direction: 'asc',
};

// ============================================================================
// 유틸 함수
// ============================================================================

/**
 * 날짜 문자열로부터 요일 인덱스 반환
 */
export function getDayOfWeekIndex(dateStr: string): number {
  const date = new Date(dateStr);
  return date.getDay();
}

/**
 * 날짜 문자열로부터 요일 라벨 반환
 */
export function getDayOfWeekLabel(dateStr: string): string {
  return DAY_OF_WEEK_LABELS[getDayOfWeekIndex(dateStr)];
}

/**
 * 요일에 따른 색상 클래스 반환
 */
export function getDayOfWeekColorClass(dayIndex: number): string {
  if (dayIndex === 0) return 'text-red-600'; // 일요일
  if (dayIndex === 6) return 'text-blue-600'; // 토요일
  return 'text-gray-600';
}

/**
 * Timestamp 포맷팅
 * V5.11: Firestore Timestamp (직렬화된 객체) 지원
 *
 * Firestore Timestamp는 Callable 함수를 통해 전달되면 다음 형태 중 하나로 직렬화됨:
 * - { seconds: number, nanoseconds: number }
 * - { _seconds: number, _nanoseconds: number }
 */
export function formatTimestamp(ts: any, format: 'date' | 'datetime' = 'datetime'): string {
  if (!ts) return '-';

  try {
    let date: Date;

    // 1. Firestore Timestamp 객체 (toDate 메서드 존재)
    if (ts.toDate && typeof ts.toDate === 'function') {
      date = ts.toDate();
    }
    // 2. 직렬화된 Firestore Timestamp ({ seconds, nanoseconds } 또는 { _seconds, _nanoseconds })
    else if (ts.seconds !== undefined || ts._seconds !== undefined) {
      const seconds = ts.seconds ?? ts._seconds;
      date = new Date(seconds * 1000);
    }
    // 3. ISO 문자열 또는 밀리초
    else {
      date = new Date(ts);
    }

    // Invalid Date 체크
    if (isNaN(date.getTime())) {
      return '-';
    }

    if (format === 'date') {
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    }

    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}
